import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Check } from "lucide-react";
import { Eyebrow, SectionHeading } from "../common/Type";
import { Reveal } from "../common/Reveal";
import Cta from "../common/Cta";
import { capabilities } from "../../lib/content";

export default function AgentFeature() {
  const [active, setActive] = useState(capabilities[0].id);
  const current = capabilities.find((c) => c.id === active);

  return (
    <section className="relative py-24 md:py-32" data-testid="section-agent-feature">
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        <div className="max-w-3xl mb-16">
          <Eyebrow className="mb-5">Mythoros©</Eyebrow>
          <SectionHeading>Your business, running inside Mythoros©.</SectionHeading>
          <p className="mt-6 font-body text-base md:text-lg text-kv-grey leading-relaxed">
            Customized around your company processes, approved documents, sales scripts, CRM workflows, service standards, industry skills, user permissions, and approved integrations.
          </p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-12 gap-px border border-kv-steel/15 bg-kv-steel/10 rounded-[14px] overflow-hidden">
          {/* Tab list */}
          <div className="lg:col-span-4 bg-kv-bg" role="tablist" aria-label="Agent capabilities">
            {capabilities.map((c) => {
              const isActive = c.id === active;
              return (
                <button
                  key={c.id}
                  role="tab"
                  aria-selected={isActive}
                  onClick={() => setActive(c.id)}
                  data-testid={`capability-tab-${c.id}`}
                  className={`w-full text-left px-6 md:px-8 py-5 border-b border-kv-steel/10 transition-colors duration-300 flex items-center gap-3 ${
                    isActive ? "bg-kv-bg2 text-kv-off" : "text-kv-grey hover:text-kv-off"
                  }`}
                >
                  <span className={`h-6 w-px transition-all duration-300 ${isActive ? "bg-kv-electric" : "bg-transparent"}`} />
                  <span className="font-display text-lg tracking-tight">{c.label}</span>
                </button>
              );
            })}
          </div>

          {/* Content */}
          <div className="lg:col-span-8 bg-kv-bg2 p-8 md:p-12 min-h-[340px]">
            <AnimatePresence mode="wait">
              <motion.div
                key={active}
                initial={{ opacity: 0, y: 16 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -12 }}
                transition={{ duration: 0.35, ease: [0.22, 1, 0.36, 1] }}
                data-testid={`capability-panel-${active}`}
              >
                <div className="font-mono text-xs uppercase tracking-[0.2em] text-kv-royal mb-6">{current.label}</div>
                <ul className="space-y-4">
                  {current.points.map((p) => (
                    <li key={p} className="flex items-start gap-3">
                      <Check className="h-5 w-5 text-kv-electric mt-0.5 shrink-0" />
                      <span className="font-body text-base md:text-lg text-kv-off leading-relaxed">{p}</span>
                    </li>
                  ))}
                </ul>
                <p className="mt-8 font-body text-sm text-kv-steel">Operates within approved scope and defined permissions.</p>
              </motion.div>
            </AnimatePresence>
          </div>
        </div>

        <Reveal className="mt-10">
          <Cta to="/contact" testId="agent-configure-cta" arrow>Configure Mythoros©</Cta>
        </Reveal>
      </div>
    </section>
  );
}
